import { Fragment, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import Pagination from "../shared/pagination";
import {
  DEFAULT_PAGE_NUMBER,
  PAGINATION_PAGE_SIZE_OPTIONS,
} from "@/app/constants/constants";
import { useQuery } from "@tanstack/react-query";
import { useGlobalNotification } from "../header/GlobalNotificationContext";
import { NotificationTypeEnum } from "@/enums/notificationTypeEnum";
import GhostList from "./ghostList";
import { GenericDataDisplayProps } from "@/app/interfaces/props/GenericDataDisplayProps";
import SortConfig from "@/app/interfaces/SortConfig";
import GenericTable from "./genericTable";
import { GenericDataDisplayTypeEnum } from "@/enums/genericDataDisplayType";

const ERROR_MESSAGE = "An unexpected error occurred.";
const GHOST_ITEM_HEIGHT = 50;

const GenericDataDisplay = <T extends Record<string, any>, F = unknown>({
  fetchPage,
  renderItem,
  itemKeyAccessor,
  queryKeyBase,
  externalFilters,
  resetOn = [],
  defaultPageSize = PAGINATION_PAGE_SIZE_OPTIONS[0],
  defaultPageNumber = DEFAULT_PAGE_NUMBER,
  pageSizeOptions = PAGINATION_PAGE_SIZE_OPTIONS,
  defaultSort = { field: "id", direction: "desc" },
  noDataMessage = "No items found.",
  wrapperStyleProps,
  cashingTime = 0,
  columns,
  mode = GenericDataDisplayTypeEnum.TABLE,
  showHeader = true,
  showPagination = true,
  isFirstColumnSticky = false,
}: GenericDataDisplayProps<T, F>) => {
  const { setNotification } = useGlobalNotification();

  const [currentPage, setCurrentPage] = useState<number>(defaultPageNumber);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [sort, setSort] = useState<SortConfig>(defaultSort);
  const resetSignature = useMemo(() => JSON.stringify(resetOn), [resetOn]);
  const [appliedResetSignature, setAppliedResetSignature] =
    useState<string>(resetSignature);
  const needsReset = appliedResetSignature !== resetSignature;

  useEffect(() => {
    if (!needsReset) {
      return;
    }

    setCurrentPage(1);
    setPageSize(defaultPageSize);
    setAppliedResetSignature(resetSignature);
  }, [needsReset, resetSignature, defaultPageSize]);

  const { data, isError, isLoading, isFetching } = useQuery({
    queryKey: [...queryKeyBase, externalFilters, currentPage, pageSize, sort],
    enabled: !needsReset,
    queryFn: () => fetchPage(currentPage, pageSize, externalFilters, sort),
    staleTime: cashingTime,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (isError) {
      setNotification(ERROR_MESSAGE, NotificationTypeEnum.ERROR, true);
    }
  }, [isError, setNotification]);

  const handlePageChange = (newPage: number) => setCurrentPage(newPage);
  const handlePageSizeChange = (newSize: number) => {
    setPageSize(newSize);
    setCurrentPage(1);
  };

  const handleSortChange = (field: string) => {
    const isAsc = sort.field === field && sort.direction === "asc";
    setSort({ field, direction: isAsc ? "desc" : "asc" });
    setCurrentPage(1);
  };

  const items = data?.items ?? [];
  const totalItems = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [currentPage, pageSize]);

  const renderData = () => {
    if (mode === GenericDataDisplayTypeEnum.TABLE && columns) {
      return (
        <GenericTable<T>
          items={items}
          columns={columns}
          sort={sort}
          onSortChange={handleSortChange}
          showHeader={showHeader}
          noDataMessage={noDataMessage}
          isLoading={isLoading || isFetching}
          isFirstColumnSticky={isFirstColumnSticky}
        />
      );
    }

    if (isLoading || isFetching) {
      return <GhostList count={pageSize} ghostHeight={GHOST_ITEM_HEIGHT} />;
    }

    if (items.length === 0) {
      return <Typography>{noDataMessage}</Typography>;
    }

    if (renderItem) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {items.map((item, index) => (
            <Fragment key={itemKeyAccessor ? itemKeyAccessor(item) : index}>
              {renderItem(item, index)}
            </Fragment>
          ))}
        </Box>
      );
    }

    return null;
  };

  return (
    <Box {...wrapperStyleProps}>
      <Box sx={{ width: "100%" }}>{renderData()}</Box>
      {showPagination && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={pageSizeOptions}
          disabled={isLoading || isFetching}
        />
      )}
    </Box>
  );
};

export default GenericDataDisplay;
